import { GithubFormat } from "../game/utils/formatCode";
import { supabase, Database } from "../../backend/supabase";
import { parseCodeFromDB } from "../../backend/useGame";
import { downloadCodeFromStorage } from "../../backend/codeStorage";
import { defaultConfig } from "../../utils/config";

export type GithubFile = {
    path: string;
    content: string;
};

export type GameCodeEntry = {
    gameId: string;
    latestCode: GithubFormat;
    earliestCode: GithubFormat;
};

export type InvalidGameReason = 'no_history_entries' | 'no_user_facing' | 'unparsable_code';

export type InvalidGameEntry = {
    gameId: string;
    reason: InvalidGameReason;
    code?: GithubFormat;
};

export type TemplateType = 'single_file' | 'phaser' | 'phaser_react' | 'invalid_format' | 'blank' | 'no_history_entries' | 'no_user_facing' | 'unparsable_code' | 'pixi_zustand' | 'multifile_unknown';

export type GameTemplateAnalysis = {
    totalCount: number;
    templateCounts: {
        'single_file': number;
        'phaser': number;
        'phaser_react': number;
        'invalid_format': number;
        'blank': number;
        'no_history_entries': number;
        'no_user_facing': number;
        'unparsable_code': number;
        'pixi_zustand': number; 
        'multifile_unknown': number; 
    };
    singleFileGameIds: string[];
    phaserGameIds: string[];
    phaserReactGameIds: string[];
    pixiZustandGameIds: string[]; 
    multifileUnknownGameIds: string[]; 
    invalidFormatGameIds: InvalidGameEntry[];
    blankGameIds: string[];
    analysisConsistent: boolean;
    finalTemplateMap?: Record<string, TemplateType>;
};

type GameRow = Database['public']['Tables']['games']['Row'] & {
    updated_at: string; // Added for stable keyset pagination
    game_history_entries: {
        id: string,
        code: unknown,
        code_url?: string,
        updated_at: string,
        created_at: string,
        user_facing: boolean | null
    }[];
};

/**
 * Parses and returns the code from a history entry object.
 * @param entry The history entry containing the code data.
 * @returns The parsed GithubFormat code.
 */
async function parseCodeFromHistoryEntry(entry: { code: unknown, code_url?: string }): Promise<GithubFormat> {
    if (defaultConfig.USE_CODE_FROM_STORAGE && entry.code_url) {
        return await downloadCodeFromStorage(entry.code_url, true);
    }
    return parseCodeFromDB(entry.code as string);
}

/**
 * Fetches all games and extracts the code from their latest and earliest user-facing history entry.
 * Games that cannot be processed are returned in a separate list with an exclusion reason.
 * Uses cursor-based pagination (keyset pagination) for IDs, and batched queries for history to avoid server limits.
 * @returns An object containing successfully parsed games, excluded games, and the total count fetched from the database.
 */
async function getAllGamesWithLatestAndEarliestHistory(onProgress?: (current: number, total: number) => void): Promise<{ validGames: GameCodeEntry[], excludedGames: InvalidGameEntry[], totalFetchedCount: number }> {
    const GAME_ID_PAGE_SIZE = 1000;
    const HISTORY_BATCH_SIZE = 100;
    
    // Step 1: Paginate and fetch ALL Game IDs first, to ensure we get the correct total count.
    let allGameIds: string[] = [];
    let lastId: string | null = null;
    let lastUpdatedAt: string | null = null;
    let hasMore = true;

    // Optional: First, get the exact count as seen by the RLS policy for debugging
    const { count: initialCount, error: countError } = await supabase
        .from('games')
        .select(`id`, { count: 'exact', head: true });
        
    if (countError) {
        console.error('Error fetching initial game count:', countError);
        throw countError;
    }
    console.log(`Initial total game count reported by client (subject to RLS): ${initialCount}`);
    
    // Start pagination
    while (hasMore) {
        let query = supabase
            .from('games')
            .select(`id, updated_at`) // Select updated_at for stable keyset
            .order('updated_at', { ascending: true }) // Order by time first
            .order('id', { ascending: true }) // Then by ID as tie breaker
            .limit(GAME_ID_PAGE_SIZE);

        if (lastUpdatedAt && lastId) {
            query = query.or(`updated_at.gt.${lastUpdatedAt},and(updated_at.eq.${lastUpdatedAt},id.gt.${lastId})`);
        }

        const { data: pageGames, error: pageGamesError } = await query;

        if (pageGamesError) {
            console.error('Error fetching game IDs page:', pageGamesError);
            throw pageGamesError;
        }

        if (!pageGames || pageGames.length === 0) {
            hasMore = false;
        } else {
            allGameIds = allGameIds.concat(pageGames.map(g => g.id));
            
            const lastGame = pageGames[pageGames.length - 1];
            lastId = lastGame.id;
            lastUpdatedAt = lastGame.updated_at;

            if (pageGames.length < GAME_ID_PAGE_SIZE) {
                hasMore = false;
            }
        }
    }

    const totalFetchedCount = allGameIds.length;
    
    if (totalFetchedCount === 0) {
        return { validGames: [], excludedGames: [], totalFetchedCount: 0 };
    }

    // Step 2: Fetch history for all games in batches
    let allGames: GameRow[] = [];
    
    // Select only the column we need based on config
    const codeColumn = defaultConfig.USE_CODE_FROM_STORAGE ? 'code_url' : 'code';
    
    // Report finding games phase
    console.log(`Fetching history options for ${totalFetchedCount} games...`);

    for (let i = 0; i < totalFetchedCount; i += HISTORY_BATCH_SIZE) {
        const batchIds = allGameIds.slice(i, i + HISTORY_BATCH_SIZE);
        
        // Fetch the game and its history using a batch of IDs
        const { data: batchGames, error: batchError } = await supabase
            .from('games')
            .select(`
                id,
                game_history_entries (id, ${codeColumn}, updated_at, created_at, user_facing)
            `)
            .in('id', batchIds); // Filter to only include IDs in the current batch

        if (batchError) {
            console.error(`Error fetching history for batch starting at index ${i}:`, batchError);
            throw batchError;
        }

        if (batchGames) {
            allGames = allGames.concat(batchGames as GameRow[]);
        }
        
        // Report progress for fetching phase (0-50% of total progress bar ideally, but for now we just show fetching count)
        if (onProgress) {
             // We can maybe say "Fetching DB data: x/y" via console, but the UI expects a clean number.
             // We will stick to the final processing loop for the main "Analyzing" progress bar to avoid confusion.
             // or we can allow the UI to jump. Let's just log for now so as not to stall.
        }
    }
    
    // Process games to find the latest and earliest user-facing code and categorize exclusions
    const excludedGames: InvalidGameEntry[] = [];
    const validGames: GameCodeEntry[] = [];
    
    const PROCESS_CONCURRENCY = 50; // Number of parallel requests for storage
    let processedCount = 0;
    let errorLogCount = 0;
    const MAX_ERROR_LOGS = 10;

    console.log(`Starting code analysis/download for ${allGames.length} games in batches of ${PROCESS_CONCURRENCY}...`);

    for (let i = 0; i < allGames.length; i += PROCESS_CONCURRENCY) {
        const chunk = allGames.slice(i, i + PROCESS_CONCURRENCY);
        
        // Process chunk in parallel
        await Promise.all(chunk.map(async (game) => {
            // Supabase returns game_history_entries as an array of rows from the related table.
            const history = game.game_history_entries as { id: string, code: unknown, code_url?: string, updated_at: string, created_at: string, user_facing: boolean | null }[];

            if (!history || history.length === 0) {
                excludedGames.push({ gameId: game.id, reason: 'no_history_entries' });
                return;
            }

            // --- FIRST HISTORY ENTRY ---
            // 1. Sort ALL history entries by created_at (ascending)
            const allEntriesSortedByCreation = [...history].sort((a, b) => 
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
            const earliestEntry = allEntriesSortedByCreation[0]; // First entry is the earliest by creation time

            // --- LATEST USER-FACING ENTRY ---
            // 2. Filter for user-facing entries and sort by updated_at
            const userFacingEntries = history
                .filter(entry => entry.user_facing === true)
                .sort((a, b) =>
                    new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime() // Sort ascending (earliest first)
                );

            if (userFacingEntries.length === 0) {
                // Note: We only check for missing LATEST code (user-facing). 
                // The EARLIEST code is always available if history.length > 0.
                excludedGames.push({ gameId: game.id, reason: 'no_user_facing' });
                return;
            }

            // Get the latest user-facing entry
            const latestEntry = userFacingEntries[userFacingEntries.length - 1]; // Last one is the latest

            try {
                // Load code for both latest and earliest
                // We await here, but since this async function is part of Promise.all, these run in parallel for the chunk
                const latestCode = await parseCodeFromHistoryEntry(latestEntry);
                const earliestCode = await parseCodeFromHistoryEntry(earliestEntry);
                
                validGames.push({
                    gameId: game.id,
                    latestCode: latestCode,
                    earliestCode: earliestCode, // Store both
                });
            } catch (e) {
                if (errorLogCount < MAX_ERROR_LOGS) {
                    console.error(`Skipping game ${game.id} due to invalid code format in history entry ${latestEntry.id} or ${earliestEntry.id}`, e);
                    errorLogCount++;
                } else if (errorLogCount === MAX_ERROR_LOGS) {
                     console.warn(`...Too many errors. Further invalid code errors will be suppressed.`);
                     errorLogCount++;
                }
                
                excludedGames.push({ 
                    gameId: game.id, 
                    reason: 'unparsable_code',
                    code: [] // Don't try to store potentially invalid code object if parsing failed hard
                });
            }
        }));
        
        processedCount += chunk.length;
        if (onProgress) {
            onProgress(processedCount, allGames.length);
        }
    }

    return { validGames, excludedGames, totalFetchedCount };
}

/**
 * Determines the template type of a game based on its files and content.
 * This logic is used for both Round 1 (latest code) and Round 2 (earliest code).
 * @param code The GithubFormat files of the game.
 * @returns The determined template type string.
 */
function determineTemplateType(code: GithubFormat, gameId: string): TemplateType {
    const files = code;
    const numFiles = files.length;
    const hasIndexHtml = files.some(f => f.path === 'index.html' || f.path === '/index.html');
    const hasIndexTsx = files.some(f => f.path === 'index.tsx' || f.path === '/index.tsx');
    const hasSrcIndexTsx = files.some(f => f.path === 'src/index.tsx' || f.path === '/src/index.tsx');

    if (numFiles === 0) {
        // Rule 0: Blank games have an empty file array.
        return 'blank';
    } else if (numFiles === 1 && hasIndexHtml) {
        // Rule 1: Single file games have only a single file in them, the index.html.
        return 'single_file';
    } else if (hasIndexTsx || hasSrcIndexTsx) {
        // Rule 2: Phaser React games have index.tsx or src/index.tsx.
        return 'phaser_react';
    } else if (numFiles > 1 && !hasSrcIndexTsx) {
        // Rule 3: Multi-file games without src/index.tsx

        // Find index.ts file content
        const indexTsFile = files.find(f => f.path === 'index.ts' || f.path === '/index.ts');
        
        if (indexTsFile) {
            const content = indexTsFile.content;
            if (content.includes('pixi') || content.includes('PIXI') || content.includes('pixi.js')) {
                return 'pixi_zustand';
            } else if (content.includes('phaser') || content.includes('Phaser')) {
                return 'phaser';
            } else {
                return 'multifile_unknown';
            }
        } else {
            console.warn(`Game ${gameId} has no index.ts file - official match: ${indexTsFile?.path}, all matches: ${files.find(f => f.path.includes('index.ts'))?.path}`);
            // Multi-file with no index.ts is categorized as multifile_unknown
            return 'multifile_unknown';
        }
        
    } else {
        // Catch-all for truly invalid/unforeseen formats that passed parsing but not logic
        return 'invalid_format';
    }
}

/**
 * Core function to analyze a list of games (already fetched) using their *latest* code to determine their template type (Round 1).
 * * @param validGames An array of successfully parsed game objects with both latest and earliest code.
 * * @param excludedGames An array of games that were excluded due to missing history or invalid code.
 * * @param totalFetchedCount The total number of games fetched from the DB (valid + excluded).
 * @returns An object containing the initial analysis report (Round 1) and a map of game IDs to their template type.
 */
function conductRound1Analysis(validGames: GameCodeEntry[], excludedGames: InvalidGameEntry[], totalFetchedCount: number): GameTemplateAnalysis {
    const analysis: GameTemplateAnalysis = {
        totalCount: totalFetchedCount,
        templateCounts: {
            'single_file': 0,
            'phaser': 0,
            'phaser_react': 0,
            'invalid_format': 0,
            'blank': 0,
            'no_history_entries': 0,
            'no_user_facing': 0,
            'unparsable_code': 0,
            'pixi_zustand': 0, 
            'multifile_unknown': 0, 
        },
        singleFileGameIds: [],
        phaserGameIds: [],
        phaserReactGameIds: [],
        pixiZustandGameIds: [], 
        multifileUnknownGameIds: [], 
        invalidFormatGameIds: [],
        blankGameIds: [],
        analysisConsistent: false,
        finalTemplateMap: {},
    };

    // 1. Process Excluded Games first
    analysis.invalidFormatGameIds = excludedGames;
    analysis.templateCounts['invalid_format'] = excludedGames.length;

    for (const excludedGame of excludedGames) {
        analysis.templateCounts[excludedGame.reason] += 1;
        // Also map excluded games, using the exclusion reason as the "template type" for final reporting/DB write
        analysis.finalTemplateMap[excludedGame.gameId] = excludedGame.reason;
    }

    // 2. Process Valid Games (Round 1: Latest Code)
    for (const game of validGames) {
        const templateType = determineTemplateType(game.latestCode, game.gameId);

        switch (templateType) {
            case 'single_file':
                analysis.singleFileGameIds.push(game.gameId);
                break;
            case 'phaser':
                analysis.phaserGameIds.push(game.gameId);
                break;
            case 'phaser_react':
                analysis.phaserReactGameIds.push(game.gameId);
                break;
            case 'pixi_zustand':
                analysis.pixiZustandGameIds.push(game.gameId);
                break;
            case 'multifile_unknown':
                analysis.multifileUnknownGameIds.push(game.gameId);
                break;
            case 'blank':
                analysis.blankGameIds.push(game.gameId);
                break;
            case 'invalid_format':
                // Catch-all for truly invalid/unforeseen formats that passed parsing but not logic
                analysis.invalidFormatGameIds.push({
                    gameId: game.gameId,
                    reason: 'unparsable_code', // Default reason for format issues
                    code: game.latestCode,
                });
                // Increment the sub-count for consistency
                analysis.templateCounts['unparsable_code'] += 1; 
                analysis.templateCounts['invalid_format'] += 1;
                break;
            default:
                // Should not happen if all cases are covered
                continue; 
        }
        
        // Increment the count for the determined template type here, only once.
        analysis.templateCounts[templateType] += 1;
        
        // Map the template type (Latest Code)
        analysis.finalTemplateMap[game.gameId] = templateType;
    }
    
    // Log the full template counts after Round 1
    console.log('Round 1 Analysis - Template Counts (Latest Code):', analysis.templateCounts);

    return analysis;
}

/**
 * Performs a Round 2 analysis on games initially categorized as 'multifile_unknown'
 * by re-checking their template using the code from the *earliest* user-facing history entry.
 * This attempts to re-attribute games to their original template before major modifications.
 * @param round1Analysis The analysis report after Round 1.
 * @param allGames A map of game IDs to their GameCodeEntry (containing earliestCode).
 * @returns The updated GameTemplateAnalysis report.
 */
function conductRound2Analysis(round1Analysis: GameTemplateAnalysis, allGames: Map<string, GameCodeEntry>): GameTemplateAnalysis {
    const analysis = { ...round1Analysis, finalTemplateMap: { ...round1Analysis.finalTemplateMap } }; // Start with Round 1 results, deep copy finalTemplateMap

    const multifileUnknownGameIds = [...analysis.multifileUnknownGameIds]; // Copy the list
    
    // Reset the count and list before re-categorization
    // The games are being re-categorized from 'multifile_unknown' into other categories,
    // or staying in 'multifile_unknown'. We reset the count/list to rebuild the final state.
    analysis.multifileUnknownGameIds = [];
    analysis.templateCounts['multifile_unknown'] = 0; 

    // Counter for logging
    let recategorizedCount = 0;

    for (const gameId of multifileUnknownGameIds) {
        const game = allGames.get(gameId);
        if (!game) {
            // Should not happen, but defensive check
            console.warn(`Game ID ${gameId} in multifile_unknown list not found in allGames map.`);
            continue;
        }

        // Round 2: Determine template type based on EARLIEST CODE
        const templateType = determineTemplateType(game.earliestCode, gameId);

        // Check if the earliest code indicates a known template
        if (templateType === 'phaser' || templateType === 'phaser_react' || templateType === 'pixi_zustand') {
            // Re-attribute to the original known type
            switch (templateType) {
                case 'phaser':
                    // We need to move the gameId from the Round 1 list (which is still in analysis.phaserGameIds
                    // from the spread operator) and add it to the final list.
                    analysis.phaserGameIds.push(gameId);
                    analysis.templateCounts['phaser'] += 1;
                    recategorizedCount += 1; // Increment counter
                    break;
                case 'phaser_react':
                    analysis.phaserReactGameIds.push(gameId);
                    analysis.templateCounts['phaser_react'] += 1;
                    recategorizedCount += 1; // Increment counter
                    break;
                case 'pixi_zustand':
                    analysis.pixiZustandGameIds.push(gameId);
                    analysis.templateCounts['pixi_zustand'] += 1;
                    recategorizedCount += 1; // Increment counter
                    break;
                default:
                    // Should not happen, but keep in multifile_unknown if the switch misses it.
                    analysis.multifileUnknownGameIds.push(gameId);
                    analysis.templateCounts['multifile_unknown'] += 1;
            }
            
            // Crucially, update the final template map with the recategorized type
            analysis.finalTemplateMap[gameId] = templateType;
            
        } else {
            // It was multifile_unknown in Round 1 AND Round 2 (or a non-multifile type). Keep it in 'multifile_unknown'.
            analysis.multifileUnknownGameIds.push(gameId);
            analysis.templateCounts['multifile_unknown'] += 1;
            
            // Update the map to reflect the final state (which is 'multifile_unknown')
            analysis.finalTemplateMap[gameId] = 'multifile_unknown';
        }
    }
    
    // The previous counts for phaser, phaser_react, and pixi_zustand from Round 1 must be adjusted.
    // The previous lists must be filtered to remove the items that were 'multifile_unknown' in R1
    // and re-categorized in R2.
    // Since we did a *copy* of the Round 1 arrays at the start of conductRound2Analysis,
    // and then added the recategorized IDs in the loop, we need to clean up the originals.
    
    // Instead of complex filtering, we should rebuild the R1 lists (phaser/phaser_react/pixi_zustand/single_file/blank/invalid_format) 
    // to only include games NOT present in multifileUnknownGameIds at the START of R2. 
    // But since the current implementation *appends* to the existing lists, a simpler fix is 
    // to trust the `finalTemplateMap` and regenerate the lists/counts.
    
    // Rebuild counts and lists based on the finalTemplateMap for consistency check:
    const finalAnalysis: GameTemplateAnalysis = {
        totalCount: analysis.totalCount,
        templateCounts: {
            'single_file': 0, 'phaser': 0, 'phaser_react': 0, 'invalid_format': 0, 
            'blank': 0, 'no_history_entries': 0, 'no_user_facing': 0, 
            'unparsable_code': 0, 'pixi_zustand': 0, 'multifile_unknown': 0,
        },
        singleFileGameIds: [], phaserGameIds: [], phaserReactGameIds: [], 
        pixiZustandGameIds: [], multifileUnknownGameIds: [], invalidFormatGameIds: [],
        blankGameIds: [], analysisConsistent: false, finalTemplateMap: analysis.finalTemplateMap
    };
    
    for (const gameId in finalAnalysis.finalTemplateMap) {
        const templateType = finalAnalysis.finalTemplateMap[gameId];
        finalAnalysis.templateCounts[templateType] += 1;

        switch (templateType) {
            case 'single_file':
                finalAnalysis.singleFileGameIds.push(gameId);
                break;
            case 'phaser':
                finalAnalysis.phaserGameIds.push(gameId);
                break;
            case 'phaser_react':
                finalAnalysis.phaserReactGameIds.push(gameId);
                break;
            case 'pixi_zustand':
                finalAnalysis.pixiZustandGameIds.push(gameId);
                break;
            case 'multifile_unknown':
                finalAnalysis.multifileUnknownGameIds.push(gameId);
                break;
            case 'blank':
                finalAnalysis.blankGameIds.push(gameId);
                break;
            case 'no_history_entries':
            case 'no_user_facing':
            case 'unparsable_code':
                // These are sub-counts of 'invalid_format'
                finalAnalysis.templateCounts['invalid_format'] += 1;
                finalAnalysis.invalidFormatGameIds.push({ gameId: gameId, reason: templateType, code: allGames.get(gameId)?.latestCode });
                break;
        }
    }
    
    // Log the recategorized count
    console.log(`Round 2 Analysis: Recategorized ${recategorizedCount} games from 'multifile_unknown' to known templates.`);
    
    // Log the full template counts after Round 2
    console.log('Round 2 Analysis - Final Template Counts (After Re-attribution):', finalAnalysis.templateCounts);


    // Final consistency check
    finalAnalysis.analysisConsistent = checkAnalysisConsistency(finalAnalysis);

    return finalAnalysis;
}


/**
 * Performs a final check to ensure the sum of all template counts equals the total count.
 * Returns a boolean indicating the consistency result.
 * @param analysis The analysis object to check.
 * @returns true if the counts match, false otherwise.
 */
function checkAnalysisConsistency(analysis: GameTemplateAnalysis): boolean {
    // The sum now only includes the final, primary categories.
    const sumOfTemplateCounts = analysis.templateCounts['single_file'] +
        analysis.templateCounts['phaser'] +
        analysis.templateCounts['phaser_react'] +
        analysis.templateCounts['invalid_format'] +
        analysis.templateCounts['blank'] +
        analysis.templateCounts['pixi_zustand'] + 
        analysis.templateCounts['multifile_unknown'];
    
    // Ensure the breakdown of invalid format matches the main count
    const sumOfInvalidSubCounts = analysis.templateCounts['no_history_entries'] +
        analysis.templateCounts['no_user_facing'] +
        analysis.templateCounts['unparsable_code'];

    const isTotalSumConsistent = sumOfTemplateCounts === analysis.totalCount;
    // Note: The sumOfInvalidSubCounts must equal the count of all gameIds that have one of the three reasons.
    // Since the finalAnalysis rebuilds the invalidFormatGameIds from the map, this should be consistent.
    const isInvalidSubCountsConsistent = sumOfInvalidSubCounts === analysis.invalidFormatGameIds.length;

    // ID array length must match its template count (phaser count includes re-attributed ones now)
    const isSingleFileConsistent = analysis.singleFileGameIds.length === analysis.templateCounts['single_file'];
    const isPhaserConsistent = analysis.phaserGameIds.length === analysis.templateCounts['phaser'];
    const isPhaserReactConsistent = analysis.phaserReactGameIds.length === analysis.templateCounts['phaser_react'];
    const isPixiZustandConsistent = analysis.pixiZustandGameIds.length === analysis.templateCounts['pixi_zustand']; 
    const isMultifileUnknownConsistent = analysis.multifileUnknownGameIds.length === analysis.templateCounts['multifile_unknown']; 
    // Note: The count for 'invalid_format' is the sum of excludedGames and potential additions in conductAnalysis.
    // The invalidFormatGameIds array stores all these excluded/invalid entries.
    const isInvalidFormatArrayConsistent = analysis.invalidFormatGameIds.length === analysis.templateCounts['invalid_format'];
    const isBlankConsistent = analysis.blankGameIds.length === analysis.templateCounts['blank'];


    const isConsistent = isTotalSumConsistent && isInvalidSubCountsConsistent &&
        isSingleFileConsistent && isPhaserConsistent && isPhaserReactConsistent &&
        isInvalidFormatArrayConsistent && isBlankConsistent &&
        isPixiZustandConsistent && isMultifileUnknownConsistent; 

    if (!isConsistent) {
        console.error('Template analysis consistency check failed!', {
            totalCount: analysis.totalCount,
            sumOfTemplateCounts: sumOfTemplateCounts,
            sumOfInvalidSubCounts: sumOfInvalidSubCounts,
            templateCounts: analysis.templateCounts,
            idListLengths: {
                singleFile: analysis.singleFileGameIds.length,
                phaser: analysis.phaserGameIds.length,
                phaserReact: analysis.phaserReactGameIds.length,
                pixiZustand: analysis.pixiZustandGameIds.length, 
                multifileUnknown: analysis.multifileUnknownGameIds.length, 
                invalidFormat: analysis.invalidFormatGameIds.length,
                blank: analysis.blankGameIds.length,
            }
        });
    }

    return isConsistent;
}


/**
 * Writes the determined template type to the 'template' column in the 'games' table 
 * for all games included in the finalTemplateMap.
 * @param finalTemplateMap A map of game IDs to their final determined template type.
 * @returns A promise that resolves when the write operation is complete, including a count of updated rows.
 */
async function writeTemplateToDB(finalTemplateMap: Record<string, TemplateType>): Promise<{ success: boolean, updatedCount: number }> {
    console.log('Starting template write to DB (using bulk_update_games RPC)...');
    const BATCH_SIZE = 1000;
    const gameIds = Object.keys(finalTemplateMap);
    const totalUpdates = gameIds.length;
    let updatedCount = 0;

    for (let i = 0; i < totalUpdates; i += BATCH_SIZE) {
        const batchIds = gameIds.slice(i, i + BATCH_SIZE);
        const batchUpdates = batchIds.map(gameId => ({
            id: gameId,
            template: finalTemplateMap[gameId],
        }));
        
        // Use the custom RPC to perform updates while preserving the timestamp
        const { error } = await supabase.rpc('bulk_update_games', {
            updates: batchUpdates
        });
            
        if (error) {
            console.error(`Error updating template for batch starting at index ${i}:`, error);
            // We can continue to the next batch even if one fails
        } else {
            updatedCount += batchUpdates.length; // Count all as successful if RPC succeeds
            console.log(`Successfully processed batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(totalUpdates / BATCH_SIZE)}. Updated so far: ${updatedCount}`);
        }
    }
    
    const success = updatedCount === totalUpdates;
    console.log(`Template write to DB finished. Total games to update: ${totalUpdates}. Successfully updated: ${updatedCount}. Success: ${success}`);
    
    return { success, updatedCount };
}

/**
 * Fetches all games from the database and analyzes them to determine their template type.
 * This is the main exported function for game template analysis, now including Round 2 and optional DB write.
 * @param writeToDB If true, the final determined template type will be written to the 'games' table.
 * * @returns A promise that resolves to an object containing the analysis report.
 */
export async function analyzeGameTemplates(writeToDB: boolean, onProgress?: (current: number, total: number) => void): Promise<GameTemplateAnalysis> {
    // Step 1: Fetch all games with their latest and earliest history entry
    const { validGames, excludedGames, totalFetchedCount } = await getAllGamesWithLatestAndEarliestHistory(onProgress);
    
    // Step 2: Conduct Round 1 analysis (based on latest code)
    const round1Analysis = conductRound1Analysis(validGames, excludedGames, totalFetchedCount);
    
    // Step 3: Conduct Round 2 analysis for 'multifile_unknown' games (based on earliest code)
    // Create a map for quick lookup in Round 2
    const validGamesMap = new Map(validGames.map(game => [game.gameId, game]));
    const finalAnalysis = conductRound2Analysis(round1Analysis, validGamesMap);
    
    // Step 4: Conditional DB Write
    if (writeToDB) {
        console.log('Write to DB flag is TRUE and analysis is CONSISTENT. Proceeding to write templates.');
        const result = await writeTemplateToDB(finalAnalysis.finalTemplateMap);
        console.log('DB Write Result:', result);
    } else {
        console.log('Write to DB flag is FALSE. Skipping template write.');
    }

    return finalAnalysis;
}